
//@ts-nocheck

'use client'

import React, { useContext, useState } from "react";

import * as S from "./Register.styles";

import { SpacerBase, Container, Row, Col } from '../../../atoms/GridSystem';
import { Paragraph, Heading } from "../../../atoms/Typography/Typography.component";
import Button from "../../../atoms/Button/Button.component";
import Icon from "../../../atoms/Icon/Icon.component";
import Input from "../../../atoms/Fields/Input/Input.component";
import Checkbox from "../../../atoms/Fields/Checkbox/Checkbox.component";
import { useModal } from "../../../../contexts/Modal.context";
import ModalAuth from "../../Modal/ModalAuth/ModalAuth.component";

import { useRouter } from "next/navigation";
import auth0 from 'auth0-js';
import { Formik, Form } from "formik";
import * as Yup from 'yup'
import Image from "next/image";
import { useTranslations, useLocale } from 'next-intl';
import { getPageLocale } from "@caffeina-siad/utils";

const passwordRegExp = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{8,})/

const initialValues = {
  name: '',
  surname: '',
  email: '',
  password: '',
  confirmPassword: '',
  companyName: '',
  relative: false,
  notRelative: false,
  relativeName: '',
  privacyAccepted: false 
}

const Register = ({ webAuthClient, authorize, setTemplate }: { webAuthClient: auth0.WebAuth | null, setTemplate: (template: string) => void }): JSX.Element => {
  const [ hasRelative, setHasRelative ] = useState(false)
  const [ privacyAccepted, setPrivacyAccepted ] = useState(false)
  const router = useRouter()
  const t = useTranslations()
  const locale = useLocale()
  const { showModal } = useModal()

  const passwordInstructions = [
    t('register.registerInstructionPasswordLength'),
    t('register.registerInstructionPasswordNumber'),
    t('register.registerInstructionPasswordUppercase'),
    t('register.registerInstructionPasswordLowercase'),
    t('register.registerInstructionPasswordSymbol')
  ]

  const formSchema = Yup.object().shape({
    name: Yup.string().required(t('register.registerFormNameError')),
    surname: Yup.string().required(t('register.registerFormSurnameError')),
    email: Yup.string().email(t('register.registerFormEmailError')).required(t('global.formErrorRequired')),
    password: Yup.string()
      .required(t('register.registerFormPasswordError'))
      .matches(passwordRegExp, t('register.registerFormPasswordRegexpError')),
    confirmPassword: Yup.string()
      .required(t('register.registerFormConfirmPasswordError'))
      .oneOf([Yup.ref('password'), null], t('register.registerFormPasseordEqual')),
    companyName: Yup.string().required(t('register.registerFormCompanyNameError')),
    relative: Yup.boolean(),
    notRelative: Yup.boolean(),
    relativeName: Yup.string().when('relative', {
      is: true,
      then: () => Yup.string().required(t('register.registerFormRelativeNameError'))
    }),
    privacyAccepted: Yup.boolean().required(t('register.registerFormPrivacyError'))
  })

  const isSubmitDisabled = (values, errors) => {
    if (Object.values(errors).length > 0) return true
    if (!values.email || !values.password || !values.confirmPassword || !values.companyName || !values.name || !values.surname || !values.privacyAccepted) return true
    
    return false
  }

  const handleRegister = ({
    name,
    surname,
    email,
    password,
    companyName,
    relative,
    notRelative,
    relativeName
  }) => {
    if (!webAuthClient) return;

    webAuthClient.signup({
      connection: 'Username-Password-Authentication',
      email,
      password,
      user_metadata: {
        name,
        surname,
        companyName,
        relative: relative ? 'true' : 'false',
        relativeName: relative ? relativeName : ''
      },
      scope: 'openid profile email create:current_user_metadata update:current_user_metadata'
    }, function (err, authResult) {
      if (err) {
        console.error('Error:', err);

        if (err.code === 'invalid_signup') {
          showModal(ModalAuth, {
            title: t('register.registerModalTitle'),
            description: t('register.registerModalEmailTaken'),
            foreground: true
          })

          return;
        }

        showModal(ModalAuth, {
          title: t('register.registerModalTitle'),
          description: t('register.registerModalGeneralError'),
          foreground: true
        })

        return;
      }

      showModal(ModalAuth, {
        title: t('register.registerModalTitle'),
        description: t('register.registerModalSuccess'),
        foreground: true
      })

      webAuthClient.client.login(
        {
          realm: 'Username-Password-Authentication', //connection name or HRD domain
          username: email,
          password,
          scope: 'openid profile email read:current_user update:current_user_metadata'
        },
        function (err, authResult) {
          if (err) {
            console.error('Error:', err)
            return
          }

          authorize(authResult.accessToken).then((res) => {            
            setTimeout(() => {
              router.refresh()
            }, 2000)
          }).catch((err) => {
            console.error('Error:', err)
          })

        }
      );
      
    });
  }

  return (
    <S.Register>
      <Container>
        <Row>
          <Col xs={12} lg={6}>
            <Formik
              initialValues={initialValues}
              validationSchema={formSchema}
              onSubmit={handleRegister}
            >
              {({ values, errors, touched, handleChange, handleSubmit, setValues }) => {
                return (
                  <Form>
                    <S.RegisterFormWrapper>
                      <Heading 
                        content={t('register.title')}
                        typo={'headingLG'}
                      />
                      <SpacerBase xs={2} />
                      <S.RegisterFormSignup>
                        <Paragraph 
                          content={t('register.subtitle')}
                          typo={'buttonMD'}
                        />
                        <S.RegisterFormSignupCTA
                          onClick={() => setTemplate('login')}
                        >
                          <Paragraph 
                            content={t('register.login')}
                            typo={'buttonMD'}
                          />
                        </S.RegisterFormSignupCTA>
                      </S.RegisterFormSignup>
                      <SpacerBase xs={3} />
                      <S.RegisterFormRow>
                        <S.RegisterFormInputWrapper>
                          <Input type="text" placeholder={t('register.registerFormNamePlaceholder')} name="name"/>
                        </S.RegisterFormInputWrapper>
                        <S.RegisterFormInputWrapper>
                          <Input type="text" placeholder={t('register.registerFormSurnamePlaceholder')} name="surname"/>
                        </S.RegisterFormInputWrapper>
                      </S.RegisterFormRow>
                      <S.RegisterFormRow>
                        <S.RegisterFormInputWrapper fullWidth>
                          <Input type="text" placeholder={t('register.registerFormEmailPlaceholder')} name="email"/>
                        </S.RegisterFormInputWrapper>
                      </S.RegisterFormRow>
                      <S.RegisterFormRow>
                        <S.RegisterFormInputWrapper fullWidth>
                          <Input type="password" placeholder={t('register.registerFormPasswordPlaceholder')} name="password"/>
                        </S.RegisterFormInputWrapper>
                      </S.RegisterFormRow>
                      {passwordInstructions.map((instruction, index) => (
                        <S.RegisterPasswordInstruction key={index}>
                          <Icon name={'check'} />
                          <Paragraph typo={'paragraphSM'} content={instruction}/>
                        </S.RegisterPasswordInstruction>
                      ))}
                      <SpacerBase xs={2} />
                      <S.RegisterFormRow>
                        <S.RegisterFormInputWrapper fullWidth>
                          <Input type="password" placeholder={t('register.registerFormConfirmPasswordPlaceholder')} name="confirmPassword"/>
                        </S.RegisterFormInputWrapper>
                      </S.RegisterFormRow>
                      <SpacerBase xs={2} />
                      <Paragraph 
                        content={t('register.registerFormCompanyNamePlaceholder')}
                        typo={'paragraphSM'}
                      />
                      <SpacerBase xs={2} />
                      <S.RegisterFormRow>
                        <S.RegisterFormInputWrapper fullWidth>
                          <Input type="text" placeholder={t('register.registerFormCompanyNamePlaceholder')} name="companyName"/>
                        </S.RegisterFormInputWrapper>
                      </S.RegisterFormRow>
                      <SpacerBase xs={2} />
                      <Paragraph 
                        content={t('register.registerFormRelative')}
                        typo={'paragraphSM'}
                      />
                      <SpacerBase xs={2} />
                      <S.RegisterFormRow>
                        <S.RegisterFormInputWrapper >
                          <Checkbox label={t('global.si')} name="relative" checked={hasRelative} onChange={() => {
                            setHasRelative(true)
                            setValues(prevValues => ({
                              ...prevValues,
                              relative: true,
                              notRelative: false
                            }))
                          }}/>
                        </S.RegisterFormInputWrapper>
                        <S.RegisterFormInputWrapper >
                          <Checkbox label={t('global.no')} name="notRelative" checked={!hasRelative} onChange={() => {
                            setHasRelative(false)
                            setValues(prevValues => ({
                              ...prevValues,
                              relative: false,
                              notRelative: true
                            }))
                          }}/>
                        </S.RegisterFormInputWrapper>
                      </S.RegisterFormRow>
                      <SpacerBase xs={2} />
                      <Paragraph 
                        content={t('register.registerFormRelativeName')}
                        typo={'paragraphSM'}
                      />
                      <SpacerBase xs={2} />
                      <S.RegisterFormRow>
                        <S.RegisterFormInputWrapper fullWidth>
                          <Input type="text" placeholder={t('register.registerFormRelativeName')} name="relativeName" disabled={!hasRelative} />
                        </S.RegisterFormInputWrapper>
                      </S.RegisterFormRow>
                      <SpacerBase xs={2} />
                      <S.RegisterPrivacyText>
                        {
                          t.rich('register.registerPrivacyText', {
                            privacy: (chunks) => {
                              const pdfFile = locale === 'it' ? '2024-06-privacy-concorso-fotografico_SIAD_ITA.pdf' : '2024-06-privacy-concorso-fotografico_SIAD_UK.pdf'

                              return (
                                <a href={`/pdf/${pdfFile}`} target="_blank" rel="noopener noreferrer">
                                  {chunks}
                                </a>
                              )
                            }
                          })
                        }
                      </S.RegisterPrivacyText>
                      <SpacerBase xs={2} />
                      <S.RegisterFormRow>
                        <S.RegisterFormInputWrapper >
                          <Checkbox label={t('register.registerFormAcceptPrivacyLabel')} name="privacyAccepted" checked={privacyAccepted} onChange={() => {
                            setPrivacyAccepted(!privacyAccepted)
                            setValues(prevValues => ({
                              ...prevValues,
                              privacyAccepted: !privacyAccepted
                            }))
                          }}/>
                        </S.RegisterFormInputWrapper>
                      </S.RegisterFormRow>
                      <SpacerBase xs={2} />
                      <Button
                        label={t('register.registerFormCta')}
                        type={'submit'}
                        fullWidth
                        iconEnd={'arrow-right'}
                        disabled={isSubmitDisabled(values, errors)}
                      />
                      <SpacerBase xs={2} />
                      <Paragraph 
                        content={t('register.registerRequiredLabel')}
                        typo={'paragraphMD'}
                      />
                      <SpacerBase xs={1} />
                    </S.RegisterFormWrapper>
                  </Form>
              )
              }}
            </Formik>
          </Col>
        </Row>
      </Container>
      <S.RegisterImageWrapper>
        <Image
          src="/images/concorso.jpg"
          alt="Register Background"
          fill
        />
      </S.RegisterImageWrapper>
    </S.Register>
  );
}

export default Register;